<template>
  <div>
    <div class="table-wrapper">
      <div v-if="isLoading" class="loading">
        <img src="@/assets/images/loading.gif" alt="Loading" />
      </div>
      <EmptyTable v-if="isEmptyTable" />
      <table v-else class="advanced-table">
        <thead>
          <tr
            v-for="headerGroup in table.getHeaderGroups()"
            :key="headerGroup.id"
          >
            <th
              v-for="header in headerGroup.headers"
              :key="header.id"
              :style="{ width: `${header.getSize()}px` }"
              :colSpan="header.colSpan"
            >
              <FlexRender
                :render="header.column.columnDef.header"
                :props="header.getContext()"
              />
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in table.getRowModel().rows"
            :key="row.id"
            :class="table.options.meta?.getRowClasses(row)"
          >
            <td v-for="cell in row.getVisibleCells()" :key="cell.id">
              <template v-if="tableType === 'template-table'">
                <template v-if="cell.column.id === 'process'">
                  <AppButton
                    style="max-width: fit-content; height: 24px !important"
                    secondary
                    @click="
                      handleFileClick(
                        cell.row.original.dealFile,
                        cell.row.original.id
                      )
                    "
                    xsm
                    title="Tekrar İndir"
                  >
                    <template #prependIcon>
                      <Icons name="Download" color="#1C1C1E" />
                    </template>
                  </AppButton>
                </template>
                <template v-else>
                  <FlexRender
                    :render="cell.column.columnDef.cell"
                    :props="cell.getContext()"
                  />
                </template>
              </template>

              <!-- File Table -->
              <template v-else="tableType === 'file-table'">
                <template v-if="cell.column.id === 'failedDealCount'">
                  <span v-if="cell.getValue()" class="error-column-item">
                    <span>
                      <img
                        width="16"
                        height="16"
                        src="@/assets/images/icons/alert-circle.svg"
                      />
                      {{ cell.getValue() }} Hata
                    </span>
                    <button
                      v-if="cell.row.original.hasFailedDeals"
                      class="report-button"
                      @click="
                        downloadDealExcelErrorReport(
                          cell.row.original.dealFile,
                          cell.row.original.id
                        )
                      "
                    >
                      Raporu İndir
                      <Icons name="Download" size="sm" color="#FF44EE" />
                    </button>
                  </span>
                  <span v-else>-</span>
                </template>
                <template v-else-if="cell.column.id === 'dealFile'">
                  <span v-if="cell.column.id === 'dealFile'">
                    <a
                      class="file-link downloadable"
                      target="_blank"
                      @click.prevent="
                        handleFileClickFromUpladTable(
                          cell.row.original.dealFile,
                          cell.row.original.id
                        )
                      "
                    >
                      <span class="icon">
                        <Icons
                          name="Download"
                          size="lg"
                          class="download-icon icon-active"
                        />
                      </span>
                      {{ cell.getValue() }}
                    </a>
                  </span>
                </template>
                <template v-else>
                  <FlexRender
                    :render="cell.column.columnDef.cell"
                    :props="cell.getContext()"
                  />
                </template>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-if="!isEmptyTable" class="pagination flex items-center gap-2">
      <Pagination
        :pageCount="table.getPageCount()"
        :clickHandler="clickHandler"
        :value="table.getState().pagination.pageIndex"
        :pageRange="5"
        firstLastButton="true"
        containerClass="button-group"
        prevLinkClass="border left rounded p-1"
        pageLinkClass="border rounded p-1"
        nextLinkClass="border right rounded p-1"
        :marginPages="0"
      />
    </div>
    <Modal
      ref="downloadErrorReportModalRef"
      @onPositiveActionClick="downloadReportModalButton()"
      positiveActionText="Tamam"
      class="download-error-report-modal"
      title="Hata Raporu İndiriliyor"
      type="info"
      withCloser="true"
    >
      <template #body>
        <p>Rapora bilgisayarınıza indirilen dosyalardan ulaşabilirsiniz.</p>
        <p>
          Excel’deki hataları düzenleyerek, şablon dosyasını yükle alanından
          tekrar yükleyebilirsiniz.
        </p>
        <div class="checkbox-wrapper">
          <Checkbox
            :onClickFn="
              (e) => (isErrorReportModalShowChecked = e.target.checked)
            "
            :checked="isErrorReportModalShowChecked"
            label="Bu açıklamayı bir daha gösterme"
          />
        </div>
      </template>
    </Modal>
  </div>
</template>

<script setup>
import { ref, watch } from "vue";
import { useStore } from "vuex";
import { AppButton, Icons, Modal, Checkbox } from "@n11/so-ui-components";
import { FlexRender, getCoreRowModel, useVueTable } from "@tanstack/vue-table";
import useBulkDealService from "../services/getBulkDealService";
import {
  getFileUploadHistoryTableColumn,
  getTemplateDownloadHistoryColumns,
} from "../services/getBulkDealTableColumn";
import Pagination from "@/components/Table/Pagination";
import EmptyTable from "./EmptyTable.vue";

const store = useStore();

const props = defineProps({
  tableType: {
    type: String,
    required: true,
  },
});

const INITIAL_PAGE_INDEX = 0;
const INITIAL_PAGE_SIZE = 10;
const pagination = ref({
  pageIndex: INITIAL_PAGE_INDEX,
  pageSize: INITIAL_PAGE_SIZE,
  reload: false,
});
const isEmptyTable = ref();
const isErrorReportModalShowChecked = ref(false);
const reportModalVisibility = ref(
  localStorage.getItem("reportModalVisibility") === "true"
);

const { data, pageCount, isLoading } = useBulkDealService(
  props.tableType,
  pagination
);
const downloadErrorReportModalRef = ref();
const getTableColumns = () => {
  let tableColumns;

  switch (props.tableType) {
    case "template-table":
      tableColumns = getTemplateDownloadHistoryColumns();
      break;
    case "file-table":
      tableColumns = getFileUploadHistoryTableColumn();
      break;
  }

  return tableColumns;
};

const table = ref(
  useVueTable({
    get data() {
      return data?.value ?? [];
    },
    get pageCount() {
      return pageCount.value;
    },
    get columns() {
      return getTableColumns();
    },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    getRowId: (row) => row.id,
    onPaginationChange: (updater) => {
      if (typeof updater === "function") {
        const updated = updater(pagination.value);
        setPagination(updated);
      } else {
        setPagination(updater);
      }
    },
    get rowCount() {
      return data?.value?.length ?? 0;
    },
    state: {
      get pagination() {
        return pagination.value;
      },
    },
  })
);

const setPagination = ({ pageIndex, pageSize }) => {
  pagination.value = {
    ...pagination.value,
    pageIndex,
    pageSize,
  };
};

watch(
  () => data.value,
  (newValue) => {
    if (newValue) {
      isEmptyTable.value = !newValue.length > 0;
    }
  },
  { immediate: true }
);

const clickHandler = (pageNum) => {
  table.value.setPageIndex(pageNum);
};

const handleFileClick = (fileName, batchId) => {
  store.dispatch("templates/dealTemplateDownloadLink", { fileName, batchId });
};
const handleFileClickFromUpladTable = (fileName, batchId) => {
  store.dispatch("templates/dealTemplateDownloadFromUpload", {
    fileName,
    batchId,
  });
};
const downloadDealExcelErrorReport = (fileName, batchId) => {
  store.dispatch("templates/dealRejectedReasonRaportDownload", { fileName, batchId });
  if (reportModalVisibility.value === false) {
    downloadErrorReportModalRef.value?.openModal();
  }
};

const downloadReportModalButton = () => {
  downloadErrorReportModalRef.value?.hiddenModal();
  if (isErrorReportModalShowChecked.value) {
    reportModalVisibility.value = true;
    localStorage.setItem("reportModalVisibility", "true");
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/variables.scss";

.table-wrapper {
  display: flex;
  gap: 16px;
  flex-direction: column;
  border: 1px solid $colorD40;
  border-radius: 8px;
  margin: 16px 0;

  .loading {
    min-height: 647px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .advanced-table {
    width: 100%;
    border-radius: 8px;
    border-collapse: separate;

    thead {
      background: $colorD10;
      tr {
        th {
          padding: 16px;
          text-align: left;
          font-weight: 600;
          color: #333;
          font-size: 13px;
        }
        th:not(:last-child) {
          border-right: 1px solid #d3d6df;
        }
      }
    }

    tbody {
      tr {
        td {
          font-weight: 600;
          padding: 9px 12px;
          text-align: left;
          border-bottom: 2px solid $colorD10;
          font-size: 13px;
          vertical-align: middle;
          .file-link {
            display: flex;
            align-items: center;
            gap: 8px;
            pointer-events: none;

            &.downloadable {
              cursor: pointer;
              pointer-events: visible;
              color: $colorM50 !important;
              text-decoration: underline;
            }

            .download-icon.icon-active {
              color: $colorM50 !important;
            }
          }

          &.failed-count-box {
            padding: 0 12px 6px;
          }
        }
        td:not(:last-child) {
          border-right: 1px solid #d3d6df;
        }

        &.success {
          background-color: $colorS05;
        }
      }
    }
  }

  .download-icon {
    color: black !important;
  }

  .download-icon-table {
    color: $colorM50 !important;
  }

  .error-column-item {
    display: flex;
    flex-direction: column;
    font-size: 12px;

    .report-button {
      display: flex;
      padding: 0;
      color: $colorM50;
      font-size: 12px;
      font-weight: 600;
    }

    span {
      color: $colorE60;
      display: flex;
      align-items: center;
      gap: 4px;
    }
  }

  .pagination {
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;
  }
}

.downloadable {
  cursor: pointer;
  color: red;
  font-size: 14px;
  font-weight: 600;
  //alti cizili yapilacak
}
.download-error-report-modal {
  p {
    line-height: 24px;
    color: $colorN80;
  }

  .checkbox-wrapper {
    margin-top: 12px;

    :deep(label) {
      font-size: 14px;
      color: $--Dark---Grey;
    }
  }
}
</style>
